import clsx from 'clsx';
import React from 'react';

const ProgressBar = (props: { progress: number }) => (
  <div style={{ backgroundColor: '#b22222', height: '6px', width: `${props.progress}%` }}></div>
);

/**
 * Use LiveProgressBar when a bar needs to be continuously updated as a user
 * scrolls. The component maintains the state of the progress bar so that when
 * the progress is updated, only it needs to rerender and not its parent.
 *
 * To use correctly, make sure the parent does not maintain progress in a state
 * variable which would trigger its own rerendering continuously defeating the
 * purpose of this component.
 */

interface LiveProgressBarProps {
  slowTransition?: boolean;
  colorOverride?: string;
}

export interface LiveProgressBarMethods {
  update: (progress: number) => void;
}

export const LiveProgressBar = React.forwardRef<LiveProgressBarMethods, LiveProgressBarProps>((props, ref) => {
  const [progress, setProgress] = React.useState(0);
  React.useImperativeHandle(ref, () => ({
    update: (newProgress: number) => setProgress(newProgress),
  }));
  return (
    <div
      className={clsx('tw-h-[6px] tw-w-full', props.colorOverride ?? 'tw-bg-red-700')}
      style={{
        transform: `scaleX(${progress / 100})`,
        transformOrigin: 'top left',
        transition: props.slowTransition ? 'all 0.4s ease-out' : undefined,
      }}
    ></div>
  );
});

/**
 * Use VideoProgressBar to show progress of video playback.
 *
 * It uses a single CSS transformation for animating the progress bar from
 * start to finish unless a playback parameter changes.
 */
interface VideoProgressBarProps {
  seek: (progress: number) => void;
}

export interface VideoProgressBarMethods {
  update: (curTime: number, duration: number, playbackRate: number, isAdvancing: boolean) => void;
}

export const VideoProgressBar = React.forwardRef<VideoProgressBarMethods, VideoProgressBarProps>((props, ref) => {
  const progressBarRef = React.useRef<HTMLDivElement>(null);
  const prevUpdate = React.useRef<{
    ts: number;
    startTime: number;
    startProgress: number;
    isAdvancing: boolean;
  } | null>(null);

  // Tracks whether a deferred animation (via requestAnimationFrame) is still
  // valid or whether it's already stale due to a newer animation.
  const animationIdRef = React.useRef(0);

  React.useImperativeHandle(ref, () => ({
    update: (curTime: number, duration: number, playbackRate: number, isAdvancing: boolean) => {
      if (!progressBarRef.current || duration === 0) {
        return;
      }

      const now = Date.now() / 1000;
      const currentProgress = Math.min(curTime / duration, 1);
      const timeRemaining = duration - curTime;

      if (prevUpdate.current === null || !isAdvancing) {
        //
        // If there's never been a previous update, just snap to the current position.
        //

        // Set current position WITHOUT transition
        progressBarRef.current.style.transitionDuration = '0s';
        progressBarRef.current.style.transform = `scaleX(${currentProgress})`;
        animationIdRef.current += 1;

        prevUpdate.current = {
          isAdvancing: false,
          startProgress: currentProgress,
          startTime: curTime,
          ts: now,
        };
      } else {
        //
        // There was a previous update, check if the previous animation is on
        // track with the current update.
        //
        const timeElapsed = now - prevUpdate.current.ts;
        const expectedTime = prevUpdate.current.isAdvancing
          ? prevUpdate.current.startTime + timeElapsed * playbackRate
          : prevUpdate.current.startTime;
        const timeDiff = Math.abs(curTime - expectedTime);
        if (timeDiff > 0.01) {
          //
          // If the time difference is more than 10ms, reset the position and animation.
          //

          // Set current position WITHOUT transition
          progressBarRef.current.style.transitionDuration = '0s';
          progressBarRef.current.style.transform = `scaleX(${currentProgress})`;
          animationIdRef.current += 1;

          prevUpdate.current = {
            isAdvancing: false,
            startProgress: currentProgress,
            startTime: curTime,
            ts: now,
          };
        } else {
          return;
        }
      }

      if (isAdvancing) {
        if (!prevUpdate.current) {
          throw Error('unexpected null prevUpdate.current');
        }
        prevUpdate.current.isAdvancing = true;

        const transitionDuration = timeRemaining / playbackRate;
        const nextAnimationId = animationIdRef.current + 1;
        animationIdRef.current = nextAnimationId;

        // Use requestAnimationFrame so that the no-duration transition can
        // complete first (if applicable) before setting the transition with
        // duration.
        requestAnimationFrame(() => {
          if (!progressBarRef.current) {
            return;
          }
          if (nextAnimationId !== animationIdRef.current) {
            return;
          }
          // Animate progress bar until end of media.
          progressBarRef.current.style.transitionDuration = `${transitionDuration}s`;
          progressBarRef.current.style.transform = `scaleX(1)`;
        });
      }
    },
  }));

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const xPos = e.clientX - e.currentTarget.getBoundingClientRect().left;
    const width = e.currentTarget.offsetWidth;
    const seekProgress = xPos / width;
    if (progressBarRef.current) {
      prevUpdate.current = null;
    }
    props.seek(seekProgress);
  };

  return (
    <div className="tw-h-[6px] tw-relative tw-w-full">
      <div
        className="tw-h-[6px] group-hover:tw-h-[18px] tw-bg-gray-900 group-hover:tw-opacity-90 tw-w-full tw-absolute tw-bottom-0 tw-left-0"
        style={{
          transitionDuration: `0.1s`,
          transitionProperty: 'height',
          transitionTimingFunction: 'linear',
        }}
        onClick={handleSeek}
      >
        <div
          ref={progressBarRef}
          className="tw-w-full tw-h-full tw-bg-red-700 tw-rounded-r-sm"
          style={{
            transform: 'scaleX(0)',
            transformOrigin: 'top left',
            transitionDuration: '0s',
            transitionProperty: 'transform',
            transitionTimingFunction: 'linear',
          }}
        />
      </div>
    </div>
  );
});

export default ProgressBar;
